// This is the parent component of a modal: ModalAdminvendorstwo

import {
  Button as AIMEButton,
  Input as AIMEInput,
} from "tot-loft-component-library";

import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import GenericModal from "./GenericModal";
import ModalAdminReward from "./ModalAdminreward";
import ModalAdminvendorbadge from "./ModalAdminvendorbadge";
import ModalAdminvendorstatus from "./ModalAdminvendorstatus";
import Navbar from "./Navbar";
import { fetchIndustrysectorparentItems } from "./redux/slices/IndustrysectorcategoriesThunks";
import { fetchAccountHistoryItem } from "./redux/slices/MembersThunks";
import { fetchQueriesItems } from "./redux/slices/QueriesThunks";
import { fetchReferralByVendorId } from "./redux/slices/ReferralsThunks";
import {
  fetchVendorById,
  fetchVendorItems,
  getVendorRewardHistory,
} from "./redux/slices/VendorThunks";

export default function Adminvendorstwo() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const industryParent = useSelector(
    (state) => state.industrysectorparent.items
  );
  const referrals = useSelector((state) => state.referrals.items);

  // Local state
  const [vendorState, setVendorState] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBadgeModalOpen, setIsBadgeModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [rewardHistory, setRewardHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [viewAll, setViewAll] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Memoized sector options
  const sectorOptions = useMemo(() => {
    return industryParent.map((val) => ({
      value: val.sector_name,
      label: val.sector_name,
    }));
  }, [industryParent]);

  // Format date helper
  const formatDate = useCallback((date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, []);

  const formatMemberSinceDate = useCallback((dateInput) => {
    if (!dateInput) return "";
    return new Date(dateInput)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, " - ");
  }, []);

  // History fetching
  const getHistory = useCallback(
    async (id) => {
      if (!id) return;
      const res = await dispatch(fetchAccountHistoryItem(id));
      if (res.payload?.length > 0) {
        setHistory(res.payload);
      }
    },
    [dispatch]
  );

  const getRewardHistory = useCallback(async () => {
    if (!id) return;
    const res = await dispatch(getVendorRewardHistory(id));
    if (res.payload?.length > 0) {
      setRewardHistory(res.payload);
    }
  }, [dispatch, id]);

  // Modal handlers
  const openModal = useCallback(() => setIsModalOpen(true), []);
  const openBadgeModal = useCallback(() => setIsBadgeModalOpen(true), []);
  const openRewardModal = useCallback(() => setIsRewardModalOpen(true), []);

  const closeRewardModal = useCallback(async () => {
    await getRewardHistory();
    setIsRewardModalOpen(false);
  }, [getRewardHistory]);

  const closeModal = useCallback(async () => {
    await getHistory();
    setIsModalOpen(false);
  }, [getHistory]);

  const closeBadgeModal = useCallback(async () => {
    await getHistory();
    setIsBadgeModalOpen(false);
  }, [getHistory]);

  const enrichedReferrals = useMemo(() => {
    if (!referrals.length) return [];

    return referrals.map((referral) => {
      return {
        ...referral,
        member: referral.member_id || null,
        vendor: referral?.vendor_id || null,
      };
    });
  }, [referrals]);

  const { tableData, tableHeaders, tableColumns } = useMemo(() => {
    const headers = [
      { label: "Referral ID", width: "17%" },
      { label: "Date", width: "13%" },
      { label: "Member", width: "14%" },
      { label: "Member Status", width: "18%" },
      { label: "Vendor Status", width: "18%" },
      { label: "Action", width: "9%" },
    ];

    const columns = [
      { key: "referral_id", width: "17%" },
      { key: "created_at", width: "13%" },
      { key: "member_name", width: "14%" },
      { key: "referral_member_status", width: "18%" },
      { key: "referral_status", width: "18%" },
      { key: "action", width: "9%" },
    ];

    if (!enrichedReferrals?.length)
      return { tableData: [], tableHeaders: headers, tableColumns: columns };

    const vendorId = String(vendorState?._id || "");
    const lowerSearch = search.toLowerCase();

    const filteredData = enrichedReferrals
      .map((r) => ({
        _id: r._id,
        referral_id: `R${r._id.slice(-7)}`,
        created_at: formatDate(r.created_at),
        member_name: r.member
          ? `${r.member.first_name} ${r.member.last_name}`
          : "",
        referral_member_status: r.referral_member_status || "",
        referral_status: r.referral_status || "",
        vendor_name: r.vendor_id?.company_name || "",
        vendor: r.vendor_id,
        dispute: r.dispute || null,
        is_urgent: r.is_urgent || null,
      }))
      .filter((r) => String(r.vendor?._id) === vendorId)
      .filter((r) =>
        !search
          ? true
          : [
              r.referral_id,
              r.member_name,
              r.vendor_name,
              r.created_at,
              r.referral_member_status,
              r.referral_status,
            ].some((field) => field?.toLowerCase().includes(lowerSearch))
      )
      .slice(0, 5);

    return {
      tableData: filteredData,
      tableHeaders: headers,
      tableColumns: columns,
    };
  }, [enrichedReferrals, vendorState, search, formatDate]);

  const handleClick = useCallback(
    (item) => {
      navigate(`/adminreferralsone/${item._id}`);
    },
    [navigate]
  );

  const displayedRewardHistory = useMemo(() => {
    const sorted = [...rewardHistory].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    return viewAll ? sorted : sorted.slice(0, 2);
  }, [rewardHistory, viewAll]);

  const handleRewardChange = useCallback((e) => {
    const newValue = e.target.value;
    if (/^\d*$/.test(newValue)) {
      setVendorState((prev) => ({ ...prev, reward: newValue }));
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(fetchIndustrysectorparentItems()),
          dispatch(fetchReferralByVendorId(id)),
          dispatch(fetchQueriesItems()),
        ]);

        const response = await dispatch(fetchVendorById({ id })).unwrap();
        setVendorState(response);

        await Promise.all([getHistory(response.user._id), getRewardHistory()]);
      } catch (err) {
        console.error("Failed to fetch vendor:", err);
      }
    };

    fetchData();
  }, [id, dispatch, getHistory, getRewardHistory]);

  const reversedHistory = history.slice();

  const visibleHistory = showAll
    ? reversedHistory
    : reversedHistory.slice(0, 5);

  return (
    <RootWrapperAdminvendorstwo>
      <Navbar />
      <Dashboard>
        <Wrapper>
          <Left_0001>
            <PRTNR.SidebarNav />
          </Left_0001>
          {!vendorState ? (
            <>Loading.....</>
          ) : (
            <Right>
              <Frame1541>
                <Frame1543>
                  <LinkButton>
                    <BackContainer onClick={() => navigate("/adminvendorsone")}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M13.3334 8H2.66675"
                          stroke="#3B5A6F"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.66675 12L2.66675 8L6.66675 4"
                          stroke="#6162C5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <BackText>Back</BackText>
                    </BackContainer>
                  </LinkButton>
                  <Frame1503>
                    <Frame1497>
                      <img
                        src={vendorState.compay_logo}
                        alt="Company Logo"
                        style={{ width: "113px", height: "83px" }}
                      />
                    </Frame1497>
                    <Frame1494>
                      <Nesti>
                        {`${vendorState.vendor_firstname} ${vendorState.vendor_lastname}`}
                      </Nesti>
                      <Vendor>Vendor</Vendor>
                      <MemberSince01Mar2025>
                        Member Since:{" "}
                        {formatMemberSinceDate(vendorState.created_at)}
                      </MemberSince01Mar2025>
                    </Frame1494>
                  </Frame1503>
                </Frame1543>
                <Frame1542>
                  <Widthdraw>
                    <Frame1422>
                      <Frame1429>
                        <Status>Status:</Status>
                        <LinkButton>
                          <BackContainer onClick={openModal}>
                            <ActiveText>
                              {vendorState.vendor_profile_status}
                            </ActiveText>
                            <svg
                              width="16"
                              height="17"
                              viewBox="0 0 16 17"
                              fill="none"
                            >
                              <path
                                d="M3.03881 11.2656C3.13699 11.0103 3.18609 10.8826 3.27029 10.8242C3.34388 10.7731 3.43493 10.7538 3.52293 10.7706C3.62362 10.7898 3.72032 10.8865 3.91372 11.0799L5.41978 12.586C5.61318 12.7794 5.70988 12.8761 5.72911 12.9767C5.74591 13.0647 5.72659 13.1558 5.67549 13.2294C5.61703 13.3136 5.48939 13.3627 5.23411 13.4609L1.66675 14.8329L3.03881 11.2656Z"
                                stroke="#3B5A6F"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M3.08964 11.1334C3.18065 10.8967 3.22615 10.7784 3.28557 10.6677C3.33835 10.5694 3.39926 10.4757 3.46768 10.3875C3.5447 10.2883 3.63433 10.1986 3.8136 10.0194L11.3334 2.49955C12.0698 1.76317 13.2637 1.76317 14.0001 2.49955C14.7365 3.23593 14.7365 4.42984 14.0001 5.16622L6.48027 12.686C6.301 12.8653 6.21136 12.9549 6.11211 13.032C6.02395 13.1004 5.93022 13.1613 5.83189 13.2141C5.7212 13.2735 5.60289 13.319 5.36626 13.41L1.66675 14.8329L3.08964 11.1334Z"
                                stroke="#6162C5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </BackContainer>
                        </LinkButton>
                      </Frame1429>
                      <Frame1429>
                        <Tag>Tag:</Tag>
                        <LinkButton>
                          <BackContainer
                            onClick={() => {
                              openBadgeModal();
                            }}
                          >
                            {vendorState?.vendor_badge == "New" ? (
                              <svg
                                width="68"
                                height="24"
                                viewBox="0 0 68 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M59.3333 4H8.33325V20H59.3333L53.3333 12L59.3333 4Z"
                                  fill="#76C065"
                                />
                                <path
                                  d="M15.3673 16V8.3H16.5443L21.3733 14.229H20.7903V8.3H22.2093V16H21.0323L16.2033 10.071H16.7863V16H15.3673ZM25.5925 11.501H29.4205V12.678H25.5925V11.501ZM25.7025 14.801H30.0475V16H24.2725V8.3H29.8935V9.499H25.7025V14.801ZM33.3492 16L30.7972 8.3H32.2822L34.5592 15.263H33.8222L36.1872 8.3H37.5072L39.8062 15.263H39.0912L41.4232 8.3H42.7872L40.2352 16H38.7172L36.6162 9.719H37.0122L34.8782 16H33.3492Z"
                                  fill="white"
                                />
                                <path
                                  d="M4.33325 6C4.33325 4.89543 5.22868 4 6.33325 4H8.33325V20H4.33325V6Z"
                                  fill="#76C065"
                                />
                                <path
                                  d="M4.33325 20L10.3333 20L10.3333 24L4.33325 20Z"
                                  fill="#4C9F39"
                                />
                              </svg>
                            ) : vendorState?.vendor_badge == "Popular" ? (
                              <svg
                                width="84"
                                height="24"
                                viewBox="0 0 84 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M70.3333 4H8.33325V20H70.3333V4Z"
                                  fill="#FD4D93"
                                />
                                <path
                                  d="M15.3673 16V8.3H18.5353C19.2173 8.3 19.8003 8.41 20.2843 8.63C20.7756 8.85 21.1533 9.16533 21.4173 9.576C21.6813 9.98667 21.8133 10.4743 21.8133 11.039C21.8133 11.6037 21.6813 12.0913 21.4173 12.502C21.1533 12.9127 20.7756 13.228 20.2843 13.448C19.8003 13.668 19.2173 13.778 18.5353 13.778H16.1593L16.7973 13.107V16H15.3673ZM16.7973 13.261L16.1593 12.568H18.4693C19.0999 12.568 19.5729 12.436 19.8883 12.172C20.2109 11.9007 20.3723 11.523 20.3723 11.039C20.3723 10.5477 20.2109 10.17 19.8883 9.906C19.5729 9.642 19.0999 9.51 18.4693 9.51H16.1593L16.7973 8.806V13.261ZM25.7278 16.077C25.1265 16.077 24.5911 15.9487 24.1218 15.692C23.6525 15.428 23.2821 15.0687 23.0108 14.614C22.7395 14.1593 22.6038 13.6423 22.6038 13.063C22.6038 12.4763 22.7395 11.9593 23.0108 11.512C23.2821 11.0573 23.6525 10.7017 24.1218 10.445C24.5911 10.1883 25.1265 10.06 25.7278 10.06C26.3365 10.06 26.8755 10.1883 27.3448 10.445C27.8215 10.7017 28.1918 11.0537 28.4558 11.501C28.7271 11.9483 28.8628 12.469 28.8628 13.063C28.8628 13.6423 28.7271 14.1593 28.4558 14.614C28.1918 15.0687 27.8215 15.428 27.3448 15.692C26.8755 15.9487 26.3365 16.077 25.7278 16.077ZM25.7278 14.9C26.0651 14.9 26.3658 14.8267 26.6298 14.68C26.8938 14.5333 27.0991 14.3207 27.2458 14.042C27.3998 13.7633 27.4768 13.437 27.4768 13.063C27.4768 12.6817 27.3998 12.3553 27.2458 12.084C27.0991 11.8053 26.8938 11.5927 26.6298 11.446C26.3658 11.2993 26.0688 11.226 25.7388 11.226C25.4015 11.226 25.1008 11.2993 24.8368 11.446C24.5801 11.5927 24.3748 11.8053 24.2208 12.084C24.0668 12.3553 23.9898 12.6817 23.9898 13.063C23.9898 13.437 24.0668 13.7633 24.2208 14.042C24.3748 14.3207 24.5801 14.5333 24.8368 14.68C25.1008 14.8267 25.3978 14.9 25.7278 14.9ZM33.4336 16.077C32.957 16.077 32.5206 15.967 32.1246 15.747C31.736 15.527 31.4243 15.197 31.1896 14.757C30.9623 14.3097 30.8486 13.745 30.8486 13.063C30.8486 12.3737 30.9586 11.809 31.1786 11.369C31.406 10.929 31.714 10.6027 32.1026 10.39C32.4913 10.17 32.935 10.06 33.4336 10.06C34.013 10.06 34.5226 10.1847 34.9626 10.434C35.41 10.6833 35.762 11.0317 36.0186 11.479C36.2826 11.9263 36.4146 12.4543 36.4146 13.063C36.4146 13.6717 36.2826 14.2033 36.0186 14.658C35.762 15.1053 35.41 15.4537 34.9626 15.703C34.5226 15.9523 34.013 16.077 33.4336 16.077ZM30.1776 18.134V10.126H31.4866V11.512L31.4426 13.074L31.5526 14.636V18.134H30.1776ZM33.2796 14.9C33.6096 14.9 33.903 14.8267 34.1596 14.68C34.4236 14.5333 34.6326 14.3207 34.7866 14.042C34.9406 13.7633 35.0176 13.437 35.0176 13.063C35.0176 12.6817 34.9406 12.3553 34.7866 12.084C34.6326 11.8053 34.4236 11.5927 34.1596 11.446C33.903 11.2993 33.6096 11.226 33.2796 11.226C32.9496 11.226 32.6526 11.2993 32.3886 11.446C32.1246 11.5927 31.9156 11.8053 31.7616 12.084C31.6076 12.3553 31.5306 12.6817 31.5306 13.063C31.5306 13.437 31.6076 13.7633 31.7616 14.042C31.9156 14.3207 32.1246 14.5333 32.3886 14.68C32.6526 14.8267 32.9496 14.9 33.2796 14.9ZM40.2047 16.077C39.706 16.077 39.266 15.9853 38.8847 15.802C38.5033 15.6113 38.2063 15.3253 37.9937 14.944C37.781 14.5553 37.6747 14.0677 37.6747 13.481V10.126H39.0497V13.294C39.0497 13.822 39.167 14.218 39.4017 14.482C39.6437 14.7387 39.9847 14.867 40.4247 14.867C40.7473 14.867 41.026 14.801 41.2607 14.669C41.5027 14.537 41.6897 14.339 41.8217 14.075C41.961 13.811 42.0307 13.4847 42.0307 13.096V10.126H43.4057V16H42.0967V14.416L42.3277 14.9C42.1297 15.2813 41.84 15.5747 41.4587 15.78C41.0773 15.978 40.6593 16.077 40.2047 16.077ZM45.206 16V7.838H46.581V16H45.206ZM52.0046 16V14.812L51.9276 14.559V12.48C51.9276 12.0767 51.8066 11.765 51.5646 11.545C51.3226 11.3177 50.956 11.204 50.4646 11.204C50.1346 11.204 49.8083 11.2553 49.4856 11.358C49.1703 11.4607 48.9026 11.6037 48.6826 11.787L48.1436 10.786C48.459 10.544 48.833 10.3643 49.2656 10.247C49.7056 10.1223 50.1603 10.06 50.6296 10.06C51.4803 10.06 52.1366 10.2653 52.5986 10.676C53.068 11.0793 53.3026 11.7063 53.3026 12.557V16H52.0046ZM50.1566 16.077C49.7166 16.077 49.3316 16.0037 49.0016 15.857C48.6716 15.703 48.415 15.494 48.2316 15.23C48.0556 14.9587 47.9676 14.6543 47.9676 14.317C47.9676 13.987 48.0446 13.69 48.1986 13.426C48.36 13.162 48.6203 12.953 48.9796 12.799C49.339 12.645 49.8156 12.568 50.4096 12.568H52.1146V13.481H50.5086C50.0393 13.481 49.724 13.558 49.5626 13.712C49.4013 13.8587 49.3206 14.042 49.3206 14.262C49.3206 14.5113 49.4196 14.7093 49.6176 14.856C49.8156 15.0027 50.0906 15.076 50.4426 15.076C50.78 15.076 51.0806 14.999 51.3446 14.845C51.616 14.691 51.8103 14.4637 51.9276 14.163L52.1586 14.988C52.0266 15.3327 51.7883 15.6003 51.4436 15.791C51.1063 15.9817 50.6773 16.077 50.1566 16.077ZM55.0673 16V10.126H56.3763V11.743L56.2223 11.27C56.3983 10.874 56.6733 10.5733 57.0473 10.368C57.4286 10.1627 57.9016 10.06 58.4663 10.06V11.369C58.4076 11.3543 58.3526 11.347 58.3013 11.347C58.25 11.3397 58.1986 11.336 58.1473 11.336C57.6266 11.336 57.2123 11.49 56.9043 11.798C56.5963 12.0987 56.4423 12.5497 56.4423 13.151V16H55.0673Z"
                                  fill="white"
                                />
                                <path
                                  d="M4.33325 20L10.3333 20L10.3333 24L4.33325 20Z"
                                  fill="#E3387D"
                                />
                                <path
                                  d="M4.33325 6C4.33325 4.89543 5.22868 4 6.33325 4H8.33325V20H4.33325V6Z"
                                  fill="#FD4D93"
                                />
                              </svg>
                            ) : vendorState?.vendor_badge == "Ending" ? (
                              <svg
                                width="80"
                                height="24"
                                viewBox="0 0 80 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M69.3333 4H8.33325V20H69.3333V4Z"
                                  fill="#FAAE5D"
                                />
                                <path
                                  d="M16.6873 11.501H20.5153V12.678H16.6873V11.501ZM16.7973 14.801H21.1423V16H15.3673V8.3H20.9883V9.499H16.7973V14.801ZM25.9374 10.06C26.4067 10.06 26.8247 10.1517 27.1914 10.335C27.5654 10.5183 27.8587 10.8007 28.0714 11.182C28.2841 11.556 28.3904 12.04 28.3904 12.634V16H27.0154V12.81C27.0154 12.2893 26.8907 11.9007 26.6414 11.644C26.3994 11.3873 26.0584 11.259 25.6184 11.259C25.2957 11.259 25.0097 11.325 24.7604 11.457C24.5111 11.589 24.3167 11.787 24.1774 12.051C24.0454 12.3077 23.9794 12.634 23.9794 13.03V16H22.6044V10.126H23.9134V11.71L23.6824 11.226C23.8877 10.852 24.1847 10.566 24.5734 10.368C24.9694 10.1627 25.4241 10.06 25.9374 10.06ZM32.6314 16.077C32.0667 16.077 31.5571 15.9523 31.1024 15.703C30.6551 15.4463 30.3031 15.0943 30.0464 14.647C29.7897 14.1997 29.6614 13.6717 29.6614 13.063C29.6614 12.4543 29.7897 11.9263 30.0464 11.479C30.3031 11.0317 30.6551 10.6833 31.1024 10.434C31.5571 10.1847 32.0667 10.06 32.6314 10.06C33.1227 10.06 33.5627 10.17 33.9514 10.39C34.3401 10.6027 34.6481 10.929 34.8754 11.369C35.1027 11.809 35.2164 12.3737 35.2164 13.063C35.2164 13.745 35.1064 14.3097 34.8864 14.757C34.6664 15.197 34.3621 15.527 33.9734 15.747C33.5847 15.967 33.1374 16.077 32.6314 16.077ZM32.7964 14.9C33.1264 14.9 33.4197 14.8267 33.6764 14.68C33.9404 14.5333 34.1494 14.3207 34.3034 14.042C34.4647 13.7633 34.5454 13.437 34.5454 13.063C34.5454 12.6817 34.4647 12.3553 34.3034 12.084C34.1494 11.8053 33.9404 11.5927 33.6764 11.446C33.4197 11.2993 33.1264 11.226 32.7964 11.226C32.4664 11.226 32.1694 11.2993 31.9054 11.446C31.6487 11.5927 31.4397 11.8053 31.2784 12.084C31.1244 12.3553 31.0474 12.6817 31.0474 13.063C31.0474 13.437 31.1244 13.7633 31.2784 14.042C31.4397 14.3207 31.6487 14.5333 31.9054 14.68C32.1694 14.8267 32.4664 14.9 32.7964 14.9ZM34.5784 16V14.614L34.6334 13.052L34.5234 11.49V7.838H35.8874V16H34.5784ZM37.6972 16V10.126H39.0722V16H37.6972ZM38.3902 9.158C38.1335 9.158 37.9208 9.07733 37.7522 8.916C37.5908 8.75467 37.5102 8.56033 37.5102 8.333C37.5102 8.09833 37.5908 7.904 37.7522 7.75C37.9208 7.58867 38.1335 7.508 38.3902 7.508C38.6468 7.508 38.8558 7.585 39.0172 7.739C39.1858 7.88567 39.2702 8.07267 39.2702 8.3C39.2702 8.542 39.1895 8.74733 39.0282 8.916C38.8668 9.07733 38.6542 9.158 38.3902 9.158ZM44.2099 10.06C44.6792 10.06 45.0972 10.1517 45.4639 10.335C45.8379 10.5183 46.1312 10.8007 46.3439 11.182C46.5565 11.556 46.6629 12.04 46.6629 12.634V16H45.2879V12.81C45.2879 12.2893 45.1632 11.9007 44.9139 11.644C44.6719 11.3873 44.3309 11.259 43.8909 11.259C43.5682 11.259 43.2822 11.325 43.0329 11.457C42.7835 11.589 42.5892 11.787 42.4499 12.051C42.3179 12.3077 42.2519 12.634 42.2519 13.03V16H40.8769V10.126H42.1859V11.71L41.9549 11.226C42.1602 10.852 42.4572 10.566 42.8459 10.368C43.2419 10.1627 43.6965 10.06 44.2099 10.06ZM51.0799 18.211C50.5372 18.211 50.0055 18.1377 49.4849 17.991C48.9715 17.8517 48.5499 17.6463 48.2199 17.375L48.8359 16.341C49.0925 16.5537 49.4152 16.7223 49.8039 16.847C50.1999 16.979 50.5995 17.045 51.0029 17.045C51.6482 17.045 52.1212 16.8947 52.4219 16.594C52.7225 16.3007 52.8729 15.857 52.8729 15.263V14.218L52.9829 12.887L52.9389 11.556V10.126H54.2479V15.109C54.2479 16.1723 53.9765 16.9533 53.4339 17.452C52.8912 17.958 52.1065 18.211 51.0799 18.211ZM50.9039 15.736C50.3465 15.736 49.8405 15.6187 49.3859 15.384C48.9385 15.142 48.5829 14.8083 48.3189 14.383C48.0622 13.9577 47.9339 13.459 47.9339 12.887C47.9339 12.3223 48.0622 11.8273 48.3189 11.402C48.5829 10.9767 48.9385 10.6467 49.3859 10.412C49.8405 10.1773 50.3465 10.06 50.9039 10.06C51.4025 10.06 51.8499 10.159 52.2459 10.357C52.6419 10.555 52.9572 10.863 53.1919 11.281C53.4339 11.699 53.5549 12.2343 53.5549 12.887C53.5549 13.5397 53.4339 14.0787 53.1919 14.504C52.9572 14.922 52.6419 15.2337 52.2459 15.439C51.8499 15.637 51.4025 15.736 50.9039 15.736ZM51.1129 14.57C51.4575 14.57 51.7655 14.5003 52.0369 14.361C52.3082 14.2143 52.5172 14.0163 52.6639 13.767C52.8179 13.5103 52.8949 13.217 52.8949 12.887C52.8949 12.557 52.8179 12.2673 52.6639 12.018C52.5172 11.7613 52.3082 11.567 52.0369 11.435C51.7655 11.2957 51.4575 11.226 51.1129 11.226C50.7682 11.226 50.4565 11.2957 50.1779 11.435C49.9065 11.567 49.6939 11.7613 49.5399 12.018C49.3932 12.2673 49.3199 12.557 49.3199 12.887C49.3199 13.217 49.3932 13.5103 49.5399 13.767C49.6939 14.0163 49.9065 14.2143 50.1779 14.361C50.4565 14.5003 50.7682 14.57 51.1129 14.57Z"
                                  fill="white"
                                />
                                <path
                                  d="M4.33325 6C4.33325 4.89543 5.22868 4 6.33325 4H8.33325V20H4.33325V6Z"
                                  fill="#FAAE5D"
                                />
                                <path
                                  d="M4.33325 20L10.3333 20L10.3333 24L4.33325 20Z"
                                  fill="#DD8F3C"
                                />
                              </svg>
                            ) : (
                              <></>
                            )}
                            <svg
                              width="16"
                              height="17"
                              viewBox="0 0 16 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.03881 11.2656C3.13699 11.0103 3.18609 10.8826 3.27029 10.8242C3.34388 10.7731 3.43493 10.7538 3.52293 10.7706C3.62362 10.7898 3.72032 10.8865 3.91372 11.0799L5.41978 12.586C5.61318 12.7794 5.70988 12.8761 5.72911 12.9767C5.74591 13.0647 5.72659 13.1558 5.67549 13.2294C5.61703 13.3136 5.48939 13.3627 5.23411 13.4609L1.66675 14.8329L3.03881 11.2656Z"
                                stroke="#3B5A6F"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                              <path
                                d="M3.08964 11.1334C3.18065 10.8967 3.22615 10.7784 3.28557 10.6677C3.33835 10.5694 3.39926 10.4757 3.46768 10.3875C3.5447 10.2883 3.63433 10.1986 3.8136 10.0194L11.3334 2.49955C12.0698 1.76317 13.2637 1.76317 14.0001 2.49955C14.7365 3.23593 14.7365 4.42984 14.0001 5.16622L6.48027 12.686C6.301 12.8653 6.21136 12.9549 6.11211 13.032C6.02395 13.1004 5.93022 13.1613 5.83189 13.2141C5.7212 13.2735 5.60289 13.319 5.36626 13.41L1.66675 14.8329L3.08964 11.1334Z"
                                stroke="#6162C5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </BackContainer>
                        </LinkButton>
                      </Frame1429>
                    </Frame1422>
                    <PRTNR.Button
                      onClick={() =>
                        navigate("/AdminVendorProfile", {
                          state: { vendor: vendorState, sector: sectorOptions },
                        })
                      }
                    >
                      View Profile
                    </PRTNR.Button>
                  </Widthdraw>
                </Frame1542>
              </Frame1541>

              <Frame1512>
                <Frame1509>
                  <Frame1496>
                    <GeneralInformation>General Information</GeneralInformation>
                    <VednorFourm>
                      <Frame1512>
                        <PRTNR.Input
                          label="First Name"
                          value={vendorState.vendor_firstname}
                          required
                          readOnly
                        />
                        <PRTNR.Input
                          label="Last Name"
                          value={vendorState.vendor_lastname}
                          required
                          readOnly
                        />
                      </Frame1512>
                      <PRTNR.Input
                        label="Phone"
                        value={vendorState.contact_number}
                        readOnly
                      />
                      <PRTNR.Input
                        label="Work email address"
                        value={vendorState.vendor_work_email}
                        readOnly
                      />
                      <IndustrySectorContainer>
                        <IndustrySectorLabel>Vendor Industry Sector <Required>*</Required></IndustrySectorLabel>
                        <PRTNR.IndustryChips
                          industries={
                            Array.isArray(vendorState.vendor_industry_sector)
                              ? vendorState.vendor_industry_sector
                              : []
                          }
                        />
                      </IndustrySectorContainer>
                    </VednorFourm>
                    <Widthdraw_0001>
                      <LinkButton>
                        <BackContainer
                          onClick={() =>
                            navigate("/add-vendor", {
                              state: {
                                vendor: vendorState,
                                sector: sectorOptions,
                              },
                            })
                          }
                        >
                          <ActiveText>Edit profile</ActiveText>
                          <svg
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                          >
                            <path
                              d="M3.03881 11.2656C3.13699 11.0103 3.18609 10.8826 3.27029 10.8242C3.34388 10.7731 3.43493 10.7538 3.52293 10.7706C3.62362 10.7898 3.72032 10.8865 3.91372 11.0799L5.41978 12.586C5.61318 12.7794 5.70988 12.8761 5.72911 12.9767C5.74591 13.0647 5.72659 13.1558 5.67549 13.2294C5.61703 13.3136 5.48939 13.3627 5.23411 13.4609L1.66675 14.8329L3.03881 11.2656Z"
                              stroke="#3B5A6F"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M3.08964 11.1334C3.18065 10.8967 3.22615 10.7784 3.28557 10.6677C3.33835 10.5694 3.39926 10.4757 3.46768 10.3875C3.5447 10.2883 3.63433 10.1986 3.8136 10.0194L11.3334 2.49955C12.0698 1.76317 13.2637 1.76317 14.0001 2.49955C14.7365 3.23593 14.7365 4.42984 14.0001 5.16622L6.48027 12.686C6.301 12.8653 6.21136 12.9549 6.11211 13.032C6.02395 13.1004 5.93022 13.1613 5.83189 13.2141C5.7212 13.2735 5.60289 13.319 5.36626 13.41L1.66675 14.8329L3.08964 11.1334Z"
                              stroke="#6162C5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </BackContainer>
                      </LinkButton>
                    </Widthdraw_0001>
                  </Frame1496>
                </Frame1509>

                <Right_0001>
                  <Frame1423>
                    <Frame1422_0001>
                      <Frame1434>
                        <Rewards>Rewards</Rewards>
                      </Frame1434>
                      <Frame1540>
                        <PRTNR.Input
                          label="Reward amount"
                          value={vendorState.reward}
                          onChange={handleRewardChange}
                        />
                        <PRTNR.Button
                          onClick={() =>
                            vendorState?.reward && openRewardModal()
                          }
                        >
                          Update
                        </PRTNR.Button>
                      </Frame1540>
                      <Frame1437>
                        <Frame1435_0001>
                          <RewardsHistory>Rewards History</RewardsHistory>
                          {displayedRewardHistory.map((val, idx) => (
                            <PRTNR.RewardHistory
                              key={val._id || idx}
                              date={val.created_at}
                              reward={val.reward}
                            />
                          ))}
                          {rewardHistory.length > 2 && (
                            <ViewAll onClick={() => setViewAll(!viewAll)}>
                              {viewAll ? "View less" : "View all"}
                            </ViewAll>
                          )}
                        </Frame1435_0001>
                      </Frame1437>
                    </Frame1422_0001>
                  </Frame1423>
                </Right_0001>
              </Frame1512>

              <TableReferrals>
                <Frame1433>
                  <Frame1431>
                    <RecentReferrals>Recent Referrals</RecentReferrals>
                  </Frame1431>
                  <Frame1432>
                    <GFieldsStacked_0005>
                      <GFieldsStacked_0006>
                        <AIMEInput
                          type="text"
                          placeholder="Search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </GFieldsStacked_0006>
                    </GFieldsStacked_0005>
                  </Frame1432>
                  <Frame1433_0001 />
                </Frame1433>
                <PRTNR.TableComponent
                  data={tableData}
                  tableColumn={tableColumns}
                  tableHeader={tableHeaders}
                  viewClick={handleClick}
                />
                <Frame1460>
                  <AIMEButton
                    size="small"
                    onClick={() => navigate("/adminreferralsone")}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexDirection: "row",
                      alignItems: "center",
                      flex: "none",
                      borderRadius: "999px",
                      width: "168px",
                      height: "36px",
                      background: "rgb(241, 245, 249)",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      boxSizing: "border-box",
                      padding: "8px 16px",
                    }}
                  >
                    <span
                      style={{
                        color: "rgb(33, 37, 67)",
                        textOverflow: "ellipsis",
                        fontSize: "14px",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: "600",
                        lineHeight: "140%",
                        textAlign: "left",
                      }}
                    >
                      View all
                    </span>
                  </AIMEButton>
                </Frame1460>
              </TableReferrals>

              {vendorState?.trusted_by?.length > 0 && (
                <TrustedBySection>
                  <Frame1433_0002>
                    <TrustedByTitle>Trusted by</TrustedByTitle>
                  </Frame1433_0002>
                  <TrustedByLogosContainer>
                    {vendorState.trusted_by.map((logoUrl, index) => (
                      <TrustedByLogo key={index}>
                        <img
                          src={logoUrl}
                          alt={`Trusted logo ${index + 1}`}
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </TrustedByLogo>
                    ))}
                  </TrustedByLogosContainer>
                </TrustedBySection>
              )}

              <>
                <ActivityNotification>
                  <Frame1433_0002>
                    <History>History</History>
                  </Frame1433_0002>

                  {[...visibleHistory]
                    .sort(
                      (a, b) => new Date(b.created_at) - new Date(a.created_at)
                    ) // ✅ latest first
                    .map((val, idx, arr) => (
                      <div key={val._id || idx} style={{ width: "100%" }}>
                        <PRTNR.AccountHistory
                          status={val.action}
                          date={val.created_at}
                          changes={val.changes}
                        />
                        {/* Add separator except after the last item */}
                        {idx < arr.length - 1 && <Seperator />}
                      </div>
                    ))}

                  {/* Show "View all" / "View less" button only if more than 5 items */}
                  {history.length > 5 && (
                    <Frame1460>
                      <ViewAll onClick={() => setShowAll(!showAll)}>
                        {showAll ? "View less" : "View all"}
                      </ViewAll>
                    </Frame1460>
                  )}
                </ActivityNotification>
              </>
            </Right>
          )}
        </Wrapper>
      </Dashboard>

      <Footer>
        <Section>
          <Section_0001>
            <Wrapper_0001>
              <Frame38>
                <_2025PaartnerLimitedAllRightsReserved>
                  © 2025 Paartner Limited. All Rights Reserved
                </_2025PaartnerLimitedAllRightsReserved>
              </Frame38>
            </Wrapper_0001>
          </Section_0001>
        </Section>
      </Footer>

      <GenericModal isOpen={isModalOpen} onClose={closeModal}>
        <ModalAdminvendorstatus
          isOpen={isModalOpen}
          onClose={closeModal}
          data={vendorState}
          setData={setVendorState}
        />
      </GenericModal>

      <GenericModal isOpen={isBadgeModalOpen} onClose={closeBadgeModal}>
        <ModalAdminvendorbadge
          isOpen={isBadgeModalOpen}
          onClose={closeBadgeModal}
          data={vendorState}
          setData={setVendorState}
        />
      </GenericModal>

      <GenericModal isOpen={isRewardModalOpen} onClose={closeRewardModal}>
        <ModalAdminReward
          isOpen={isRewardModalOpen}
          onClose={closeRewardModal}
          data={vendorState}
          setData={setVendorState}
          oldData={(vendor) =>
            navigate(`/adminvendorsone/${vendor._id}`, {
              state: { sector: sectorOptions },
            })
          }
        />
      </GenericModal>
    </RootWrapperAdminvendorstwo>
  );
}

const RootWrapperAdminvendorstwo = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 24px;
  width: 1200px;
  box-sizing: border-box;
  padding: 32px 0px;
`;

const Left_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  width: 298px;
  box-sizing: border-box;
`;

const Frame1437 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 24px;
  width: 878px;
  box-sizing: border-box;
`;

const Frame1541 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1543 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 8px;
  width: 666px;
  box-sizing: border-box;
`;

const LinkButton = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Frame1503 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 31px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1497 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  border-radius: 200px;
  width: 103px;
  height: 103px;
  background: rgb(255, 255, 255),
    url(https://s3-alpha-sig.figma.com/img/b9ff/3fc2/15511f081fd21537b0254ea743c17e66?Expires=1748822400&Key-Pair-Id=APKAQ4GOSFWCW27IBOMQ&Signature=ARsVBj3m5ZdmQZ0wXH~7acHcP-Xnhpk~ovw-5Ps3XwlhtFuJrTVOFfamVK6q6f1SlMY~84Juga2RJScnzD1bfR-QtFUGBR5IyA6n7CbBBNn4RNk5X4bFpGSzFY772npzujxtYYRQBAZ77gkos~VcUInvBVZfEVkhzfJmiLZPCqhSce2T8cfWodJqmzDzI8k3bIlezYiI9CAcD44LqUSClW0pitiV7-hr43QAfAs8HZDz2tgMnesgngr0OKtZyhl2TFztyZoL3wByOHCFHF4vQUmtnYdrUkMie1AaWrbYa4keiDpAJ5w6HE5rkeNlg9NnsnWLFyoLboYD93Ri6KekSA__),
    rgb(255, 255, 255);
  background-repeat: no-repeat, no-repeat, no-repeat;
  background-size: contain, cover, contain;
  background-position: center, center, center;
  box-sizing: border-box;
`;

const Frame1494 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 532px;
  box-sizing: border-box;
`;

const Nesti = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 30px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-align: left;
`;

const Vendor = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const MemberSince01Mar2025 = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1542 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Widthdraw = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  flex: none;
  gap: 16px;
  box-sizing: border-box;
`;

const Frame1422 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px 24px;
  flex-shrink: 0;
`;

const Frame1429 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: none;
  box-sizing: border-box;
`;

const Status = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Tag = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 45px;
`;

const Frame1512 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1509 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  flex: 1;
  gap: 21px;
  width: 536px;
  box-sizing: border-box;
`;

const Frame1496 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border: solid 1px rgb(225, 228, 233);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px;
  flex-shrink: 0;
`;

const GeneralInformation = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  text-align: left;
`;

const VednorFourm = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Widthdraw_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  box-sizing: border-box;
`;

const Right_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  width: 326px;
  box-sizing: border-box;
`;

const Frame1423 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1422_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  width: 326px;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
`;

const Frame1434 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Rewards = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 278px;
  min-height: 29px;
  flex: 1;
`;

const Frame1540 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 0px 0px 24px;
  flex-shrink: 0;
`;

const Frame1435_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  width: 100%;
  box-sizing: border-box;
`;

const RewardsHistory = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 280px;
  min-height: 29px;
  flex: 1;
`;

const TableReferrals = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Frame1433 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 11px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1431 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 292px;
  box-sizing: border-box;
`;

const RecentReferrals = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1432 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const GFieldsStacked_0005 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  box-sizing: border-box;
`;

const GFieldsStacked_0006 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 4px;
  box-sizing: border-box;
`;

const Frame1433_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Frame1460 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const ActivityNotification = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Frame1433_0002 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const History = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 830px;
  min-height: 29px;
  flex: 1;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Section = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  background: rgb(33, 37, 67);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Section_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame38 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const _2025PaartnerLimitedAllRightsReserved = styled.span`
  color: white;
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const BackContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;
const BackText = styled.span`
  color: #64748b;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  line-height: 140%;
  text-align: left;
`;
const ViewAll = styled.span`
  color: #6162c5;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  line-height: 140%;
  text-align: right;
  cursor: pointer;
`;

const ActiveText = styled.span`
  color: #6162c5;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  line-height: 140%;
  text-align: left;
  margin-left: 12px;
`;

const Seperator = styled.div`
  height: 1px;
  background-color: rgba(136, 136, 136, 0.1);
  width: 100%;
`;

const TrustedBySection = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const TrustedByTitle = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 830px;
  min-height: 29px;
  flex: 1;
`;

const TrustedByLogosContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  align-self: stretch;
  width: 100%;
  box-sizing: border-box;
`;

const TrustedByLogo = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: 1;
  min-width: 120px;
  height: 80px;
  border-radius: 8px;
  border: solid 1px rgb(225, 228, 234);
  background: rgb(255, 255, 255);
  box-sizing: border-box;
  padding: 8px;
`;

const IndustrySectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const IndustrySectorLabel = styled.label`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: normal;
  text-align: left;
`;

const Required = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;
